import { Badge } from "@/components/ui/badge";
import { CheckCircle, CircleNotch, XCircle, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";

const STAGE_LABELS = {
  validate: "Checking the steps",
  create_boo: "Creating the bill of operations",
  consistency_check: "SAP consistency check",
  rollback_boo: "Rolling back",
  attach_to_model: "Attaching to the production model",
  restore_byproducts: "Re-applying the by-product assignment",
  verify: "Reading it back from SAP",
  failed: "Stopped",
};

const ALL_STAGES = ["validate", "create_boo", "consistency_check", "attach_to_model", "verify"];

export const PostProgress = ({ run, onVerifyByproducts, verifying }) => {
  if (!run) return null;
  const done = run.stages || [];
  const doneNames = done.map((s) => s.stage);
  const pending = run.status === "running" ? ALL_STAGES.filter((s) => !doneNames.includes(s)) : [];
  return (
    <div className="rounded-lg border border-[#D0D5DD] bg-white p-4" data-testid="routing-post-progress">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-[14px] font-semibold">
          Posting {run.boo_id} to {run.model_id}
        </h3>
        <Badge
          variant="outline"
          className={`text-[10px] ${
            run.status === "done"
              ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"
              : run.status === "failed"
              ? "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"
              : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
          }`}
          data-testid="routing-post-status"
        >
          {run.status === "running" ? "Working" : run.status === "done" ? "Done" : "Stopped"}
        </Badge>
      </div>

      <ol className="mt-3 space-y-2">
        {done.map((stage, i) => (
          <li key={i} className="flex items-start gap-2" data-testid={`routing-stage-${stage.stage}`}>
            {stage.ok ? (
              <CheckCircle size={16} weight="fill" className="text-[#12B76A] mt-0.5 shrink-0" />
            ) : (
              <XCircle size={16} weight="fill" className="text-[#F04438] mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-[#1D2939]">
                {STAGE_LABELS[stage.stage] || stage.stage}
              </div>
              {(stage.messages || []).map((message, j) => (
                <div key={j} className="text-[12px] text-[#475467]">{message}</div>
              ))}
            </div>
          </li>
        ))}
        {pending.map((stage) => (
          <li key={stage} className="flex items-center gap-2 opacity-60">
            <CircleNotch size={16} className="text-[#98A2B3] animate-spin shrink-0" />
            <span className="text-[13px] text-[#667085]">{STAGE_LABELS[stage]}</span>
          </li>
        ))}
      </ol>

      {run.message && (
        <p
          className={`mt-3 text-[13px] ${run.ok ? "text-[#027A48]" : "text-[#B42318]"}`}
          data-testid="routing-post-message"
        >
          {run.message}
        </p>
      )}

      {run.manual_actions?.length > 0 && (
        <ByproductFollowUp run={run} onVerify={onVerifyByproducts} verifying={verifying} />
      )}

      {run.byproduct_check?.all_assigned && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-[#ABEFC6] bg-[#F6FEF9] px-3 py-2"
          data-testid="routing-byproduct-confirmed"
        >
          <CheckCircle size={16} weight="fill" className="text-[#12B76A] mt-0.5 shrink-0" />
          <div className="text-[12px] text-[#027A48]">
            <span className="font-semibold">By-product confirmed in SAP.</span>{" "}
            {run.byproduct_check.byproducts
              .map((b) => `${b.product_id} at ${b.element_id} / activity ${b.activity_id}`)
              .join(", ")}
            {run.byproduct_check.byproducts.some((b) => b.quantity_differs) && (
              <span className="block text-[#B54708]">
                Quantity differs from the old routing - check it is what you intended.
              </span>
            )}
          </div>
        </div>
      )}

      {run.verification && <Verification verification={run.verification} />}
    </div>
  );
};

const ByproductFollowUp = ({ run, onVerify, verifying }) => {
  const checked = run.byproduct_check;
  const rows = (run.byproduct_plan || []).filter((p) => p.element_id);
  return (
    <div
      className="mt-3 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] px-3 py-3"
      data-testid="routing-manual-actions"
    >
      <div className="flex items-center gap-2">
        <WarningCircle size={15} weight="fill" className="text-[#B54708] shrink-0" />
        <div className="text-[12px] font-semibold text-[#B54708]">
          One step SAP keeps for itself
        </div>
      </div>
      <p className="mt-1 text-[12px] text-[#B54708]">
        SAP will not let any API write a by-product assignment, and it drops the old one with the old
        routing. Set it on the model and this will check SAP for you - the routing is not finished until
        it does.
      </p>

      <div className="mt-2 space-y-1.5">
        {rows.map((row) => {
          const result = checked?.byproducts?.find((b) => b.product_id === row.product_id);
          return (
            <div
              key={row.product_id}
              className="rounded-md border border-[#FEDF89] bg-white px-3 py-2"
              data-testid={`routing-byproduct-todo-${row.product_id}`}
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                <span className="font-mono font-semibold text-[#1D2939]">{row.product_id}</span>
                {row.quantity && <span className="text-[#475467]">qty {row.quantity}</span>}
                <span className="text-[#475467]">
                  at <span className="font-mono">{row.element_id}</span> / activity{" "}
                  <span className="font-mono">{row.activity_id}</span>
                </span>
                {result && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      result.assigned
                        ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"
                        : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"
                    }`}
                  >
                    {result.assigned ? "found in SAP" : "not in SAP yet"}
                  </Badge>
                )}
              </div>
              {result && !result.assigned && result.found_elsewhere_at && (
                <div className="mt-0.5 text-[11px] text-[#B42318]">
                  SAP has it at {result.found_elsewhere_at} instead.
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-2 rounded-md bg-white/70 px-3 py-2 text-[11px] text-[#667085]">
        Production Models → <span className="font-mono">{run.model_id}</span> → Bill of Operations →
        Product Assignment → pick the activity → Assign By-Product → Save
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onVerify}
          disabled={verifying || !onVerify}
          className="h-8 rounded-md bg-[#B54708] px-3 text-[12px] font-medium text-white transition-colors hover:bg-[#93370D] disabled:opacity-60"
          data-testid="routing-byproduct-verify"
        >
          {verifying ? "Checking SAP…" : "I have set it - check SAP"}
        </button>
        {checked && (
          <span className="text-[11px] text-[#667085]" data-testid="routing-byproduct-checked-at">
            Last checked {new Date(checked.checked_at).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

const Verification = ({ verification }) => (
  <div className="mt-4 rounded-lg border border-[#ABEFC6] bg-[#F6FEF9] p-3" data-testid="routing-verification">
    <div className="flex items-center gap-2">
      <ArrowSquareOut size={14} className="text-[#027A48]" />
      <span className="text-[12px] font-semibold text-[#027A48]">Read back from SAP</span>
    </div>
    <div className="mt-2 overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="text-[#475467]">
            {["Operation", "Description", "Type", "Resource", "Logistics area"].map((h) => (
              <th key={h} className="text-left font-medium pr-3 pb-1">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(verification.operations || []).map((op) => (
            <tr key={op.operation_id} className="border-t border-[#D1FADF]">
              <td className="pr-3 py-1 font-mono text-[11px]">{op.operation_id}</td>
              <td className="pr-3 py-1">{op.description || "\u2014"}</td>
              <td className="pr-3 py-1">{op.type_label}</td>
              <td className="pr-3 py-1">{op.resource_id || "\u2014"}</td>
              <td className="pr-3 py-1">{op.logistics_area_id || "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {(verification.consumption_points || []).length > 0 && (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-[#475467]">Components consumed at:</span>
        {verification.consumption_points.map((point) => (
          <Badge key={point.element_id} variant="outline" className="text-[10px] bg-white text-[#027A48] border-[#ABEFC6]">
            {point.element_id} · {point.confirmation_label}
          </Badge>
        ))}
      </div>
    )}
    {(verification.byproducts || []).length > 0 && (
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="routing-verification-byproducts">
        <span className="text-[11px] text-[#475467]">By-products:</span>
        {verification.byproducts.map((byproduct) => (
          <Badge key={byproduct.product_id} variant="outline" className="text-[10px] bg-white text-[#027A48] border-[#ABEFC6]">
            {byproduct.product_id} · {byproduct.element_id || "unassigned"}
          </Badge>
        ))}
      </div>
    )}
  </div>
);
